import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Button,
  Container,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Paper,
  Grid
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, CloudUpload as CloudUploadIcon, Image as ImageIcon } from '@mui/icons-material';
import {
  getCustomerRequirementById,
  createCustomerRequirement,
  updateCustomerRequirement
} from '../../services/customerRequirementService';

interface CustomerRequirementFormData {
  name: string;
  description: string;
  icon_url: string;
  iconFile: File | null;
  is_mandatory: boolean;
  status: boolean;
  display_order: number;
}

const CustomerRequirementsForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<CustomerRequirementFormData>({
    name: '',
    description: '',
    icon_url: '',
    iconFile: null,
    is_mandatory: false,
    status: true,
    display_order: 0
  });

  useEffect(() => {
    const fetchRequirement = async () => {
      if (!id) return;
      try {
        const requirement = await getCustomerRequirementById(parseInt(id));

        // Handle both image_url and icon_url from the API
        const iconUrl = requirement.icon_url || requirement.image_url || '';

        setFormData({
          name: requirement.name,
          description: requirement.description || '',
          icon_url: iconUrl,
          is_mandatory: requirement.is_mandatory || false,
          status: requirement.status !== undefined ? requirement.status : true,
          display_order: requirement.display_order || 0,
          iconFile: null
        });
      } catch (error) {
        console.error('Error fetching requirement:', error);
        //alert('Failed to load requirement data');
      }
    };

    fetchRequirement();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>) => {
    const target = e.target as HTMLInputElement;
    const name = target.name;

    if (target.type === 'checkbox') {
      const checked = (target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: target.value
      }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (target.files && target.files[0]) {
      const file = target.files[0];
      setFormData(prev => ({
        ...prev,
        iconFile: file,
        icon_url: URL.createObjectURL(file)
      }));
    }
  };

  // Clean up object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      if (formData.icon_url && formData.icon_url.startsWith('blob:')) {
        URL.revokeObjectURL(formData.icon_url);
      }
    };
  }, [formData.icon_url]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();

      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('is_mandatory', formData.is_mandatory ? '1' : '0');
      formDataToSend.append('status', formData.status ? '1' : '0');
      formDataToSend.append('display_order', formData.display_order.toString());

      if (formData.iconFile) {
        formDataToSend.append('icon_url', formData.iconFile);
      } else if (formData.icon_url && !formData.icon_url.startsWith('blob:')) {
        const url = new URL(formData.icon_url, window.location.origin);
        formDataToSend.append('icon_url', url.pathname + url.search + url.hash);
      }

      if (isEditMode && id) {
        await updateCustomerRequirement(parseInt(id), formDataToSend);
      } else {
        await createCustomerRequirement(formDataToSend);
      }

      navigate('/customer-requirements');
    } catch (error) {
      console.error('Error saving requirement:', error);
      alert('Failed to save requirement');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ m:0, p:0 }}>
      <Box >
        <Typography variant="h4" gutterBottom>
          {isEditMode ? 'Edit' : 'Add New'} Customer Requirement
        </Typography>

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Display Order"
                  name="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="is_mandatory"
                      checked={formData.is_mandatory}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="Is Mandatory"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="status"
                      checked={formData.status}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="Active"
                  sx={{ ml: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Requirement Icon
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    <Box sx={{
                      width: 100,
                      height: 100,
                      border: '1px dashed #ccc',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      backgroundColor: '#f9f9f9'
                    }}>
                      {formData.icon_url ? (
                        <img
                          src={formData.icon_url}
                          alt="Preview"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <ImageIcon color="action" sx={{ fontSize: 30, opacity: 0.5 }} />
                      )}
                    </Box>
                    <Box>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                      >
                        {formData.icon_url ? 'Change Icon' : 'Upload Icon'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </Button>
                      {formData.icon_url && (
                        <Button
                          color="error"
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            setFormData(prev => ({
                              ...prev,
                              icon_url: '',
                              iconFile: null
                            }));
                          }}
                          sx={{ ml: 1 }}
                        >
                          Remove
                        </Button>
                      )}
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                        Recommended size: 100x100px. SVG, PNG or ICO format preferred.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/customer-requirements')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                >
                  {isEditMode ? 'Update' : 'Save'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default CustomerRequirementsForm;
